import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { heroStats } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';

const EnsoCircleLarge = () => (
  <svg viewBox="0 0 500 500" className="w-[400px] h-[400px] sm:w-[500px] sm:h-[500px] lg:w-[650px] lg:h-[650px]" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M420 250C420 343.88 343.88 420 250 420C156.12 420 80 343.88 80 250C80 156.12 156.12 80 250 80C310 80 362 110 395 155" stroke="#c9a96e" strokeWidth="2" strokeLinecap="round" className="enso-draw" strokeDasharray="1100" strokeDashoffset="1100" />
  </svg>
);

export default function HeroSection() {
  const { t } = useLanguage();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const headlines = [
    { text: t('hero_h1_1'), gold: false },
    { text: t('hero_h1_2'), gold: true },
    { text: t('hero_h1_3'), gold: false },
  ];

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-center hero-pattern overflow-hidden">
      <div className="absolute right-[-100px] lg:right-[-50px] top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none hidden sm:block">
        <EnsoCircleLarge />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0c] to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div
          className={`flex items-center gap-3 mb-6 sm:mb-8 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}
        >
          <div className="h-px w-8 sm:w-12 bg-[#c9a96e]" />
          <span className="text-[#c9a96e] text-[10px] sm:text-xs font-semibold tracking-[0.2em] sm:tracking-[0.25em] uppercase">
            {t('hero_label')}
          </span>
        </div>

        <div className="max-w-3xl">
          {headlines.map((line, i) => (
            <h1
              key={i}
              className={`font-heading font-bold leading-[1.1] tracking-tight ${line.gold ? 'text-gold-gradient' : 'text-[#f0ebe3]'} ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{
                fontSize: 'clamp(2rem, 6vw, 5rem)',
                transition: `opacity 0.8s ease ${0.3 + i * 0.15}s, transform 0.8s ease ${0.3 + i * 0.15}s`,
              }}
            >
              {line.text}
            </h1>
          ))}
        </div>

        <p
          className={`max-w-2xl text-[#8a857e] text-sm sm:text-base lg:text-lg leading-relaxed mt-6 sm:mt-8 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transition: 'opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s' }}
        >
          {t('hero_sub')}
        </p>

        <div
          className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 sm:mt-10 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transition: 'opacity 0.8s ease 1s, transform 0.8s ease 1s' }}
        >
          <Button onClick={(e) => scrollTo(e, '#services')} className="bg-[#c9a96e] text-[#0a0a0c] hover:bg-[#d4bc8a] font-medium text-sm px-6 sm:px-8 py-5 sm:py-6 tracking-wide w-full sm:w-auto justify-center">
            {t('hero_cta_services')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button variant="outline" onClick={(e) => scrollTo(e, '#contact')} className="border-[#2a2a30] text-[#f0ebe3] hover:border-[#c9a96e] hover:text-[#c9a96e] bg-transparent font-medium text-sm px-6 sm:px-8 py-5 sm:py-6 tracking-wide w-full sm:w-auto justify-center">
            {t('hero_cta_contact')}
          </Button>
        </div>

        <div
          className={`flex flex-wrap gap-8 sm:gap-12 mt-14 sm:mt-20 pt-8 sm:pt-10 border-t border-[#1a1a1f] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transition: 'opacity 0.8s ease 1.2s, transform 0.8s ease 1.2s' }}
        >
          {heroStats.map((stat, i) => (
            <div key={i}>
              <p className="text-[#c9a96e] font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">{stat.value}</p>
              <p className="text-[#6a6560] text-xs sm:text-sm mt-1 tracking-wide">{t(stat.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#4a4540]">
        <span className="text-[10px] tracking-[0.3em] uppercase">{t('hero_scroll')}</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
}
